import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { ScopeTree } from '../../..';
import { DeleteUserModal } from './delete-user-modal';
import {
  ControllerModal,
  CircularProgress,
  DialogContent,
  Box,
  Avatar,
  Stack,
  Typography,
  DialogActions,
  Tooltip,
  Button,
} from '@bymason-private/legacy-pattern-components';
import { UserModel, useGetScopes, useUpdateScope } from '@bymason/controller-api-hooks';

interface EditUserModalProps {
  open: boolean;
  user: UserModel;
  onClose: () => void;
  onSuccess?: (user: UserModel, type: 'updated' | 'deleted') => void;
  onError?: (message: string, code?: number) => void;
}

export function EditUserModal({ onClose, open, user, onSuccess, onError }: EditUserModalProps) {
  const { authToken = '' } = useAuth();
  const [getScopes, scopes, scopeError, scopeBusy] = useGetScopes(authToken);
  const [updateScopes, updateScopeResult, updateScopeError, updateScopeBusy] = useUpdateScope(authToken);

  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [checked, setChecked] = useState<string[]>(user.scopes ?? []);

  const handleUpdate = useCallback(() => {
    updateScopes([
      {
        group: 'account',
        name: 'global',
        userid: user.user_id,
        scopes: checked ?? [],
      },
    ]);
  }, [checked, updateScopes, user.user_id]);

  const handleDeleteUserModalOpen = () => setShowDeleteUserModal(true);

  const handleDeleteUserModalClose = () => setShowDeleteUserModal(false);

  const handleDeleteUserSuccess = useCallback(
    (deletedUser) => {
      setShowDeleteUserModal(false);
      onSuccess && onSuccess(deletedUser, 'deleted');
      onClose();
    },
    [onClose, onSuccess]
  );

  useEffect(() => {
    if (updateScopeResult) {
      onSuccess && onSuccess(user, 'updated');
      onClose();
    }
  }, [user, updateScopeResult, onSuccess, onClose]);

  useEffect(() => {
    if (!updateScopeBusy && updateScopeError) {
      onError && onError(updateScopeError);
    }
  }, [updateScopeError, updateScopeBusy, onError]);

  useEffect(() => {
    if (!scopeBusy && scopeError) {
      onError && onError(scopeError);
    }
  }, [scopeError, scopeBusy, onError]);

  useEffect(() => {
    getScopes({});
  }, [getScopes]);

  return (
    <ControllerModal onClose={onClose} open={open} title='Edit User'>
      <DialogContent>
        <Box my={1} display='flex' alignItems='center'>
          <Avatar src={user.picture} sx={{ mr: 2, my: 0.5 }} />
          <Stack>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Typography>{user.name}</Typography>
            </Box>
            <Typography fontWeight={700}>{user.email}</Typography>
          </Stack>
        </Box>
        <hr />
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={700}>Scopes</Typography>
          {scopeBusy ? (
            <Box display='flex' columnGap={2}>
              <span>Loading scopes</span>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <ScopeTree checked={checked} setChecked={setChecked} scopes={scopes} userScopes={user.scopes} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Tooltip title='Cannot delete root user' followCursor>
          <span>
            <Button color='error' onClick={handleDeleteUserModalOpen} size='medium' variant='text'>
              Delete user
            </Button>
          </span>
        </Tooltip>

        <Box sx={{ flex: 1 }} />
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button color='primary' onClick={handleUpdate} size='medium' variant='contained'>
          Update
        </Button>
      </DialogActions>
      {showDeleteUserModal && (
        <DeleteUserModal
          user={user}
          onClose={handleDeleteUserModalClose}
          onSuccess={handleDeleteUserSuccess}
          open={showDeleteUserModal}
        />
      )}
    </ControllerModal>
  );
}
